<template>
  <div>
    <main-navigation></main-navigation>

    <div class="dash-page">
      <div class="dash-shell">
        <!-- HEADER -->
        <div class="dash-head">
          <div>
            <h1 class="dash-head__title">Tenants</h1>
            <p class="dash-head__sub">
              {{ uniqueTenants.length }} tenant{{ uniqueTenants.length === 1 ? "" : "s" }} across all your properties
            </p>
          </div>
          <div class="dash-head__actions">
            <div class="tl-search">
              <i class="bi bi-search"></i>
              <input
                v-model="search"
                type="text"
                placeholder="Search by name, phone or ID..."
              />
            </div>
            <router-link to="/add/tenant" class="dash-btn dash-btn--primary">
              <i class="bi bi-plus-lg"></i> Add tenant
            </router-link>
          </div>
        </div>

        <!-- STATS -->
        <div v-if="uniqueTenants.length" class="dash-grid tl-stats">
          <div class="dash-stat">
            <span class="dash-stat__label">Tenants</span>
            <p class="dash-stat__value">{{ uniqueTenants.length }}</p>
          </div>
          <div class="dash-stat">
            <span class="dash-stat__label">Male</span>
            <p class="dash-stat__value">{{ genderStats.male }}</p>
          </div>
          <div class="dash-stat">
            <span class="dash-stat__label">Female</span>
            <p class="dash-stat__value">{{ genderStats.female }}</p>
          </div>
          <div class="dash-stat">
            <span class="dash-stat__label">Units occupied</span>
            <p class="dash-stat__value">{{ totalUnits }}</p>
          </div>
        </div>

        <!-- LOADING -->
        <div v-if="loading" class="dash-state">
          <div class="dash-spinner"></div>
          <p>Loading tenants...</p>
        </div>

        <!-- TABLE -->
        <div v-else-if="filteredUniqueTenants.length" class="tl-table-wrap">
          <table class="tl-table">
            <thead>
              <tr>
                <th>Tenant</th>
                <th>Phone</th>
                <th>Gender</th>
                <th>Status</th>
                <th>Units</th>
                <th class="text-end">Actions</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="tenant in filteredUniqueTenants" :key="tenant.id || tenant.tenant_idnumber">
                <td>
                  <router-link :to="`/tenant/${tenant.id}`" class="tl-tenant">
                    <span
                      class="tl-avatar"
                      :style="{ background: avatarColor(tenant.tenant_name).bg, color: avatarColor(tenant.tenant_name).ink }"
                    >
                      {{ getInitials(tenant.tenant_name) }}
                    </span>
                    <span class="tl-tenant-text">
                      <span class="tl-tenant-name">{{ tenant.tenant_name || "Unnamed" }}</span>
                      <span class="tl-tenant-id">ID {{ tenant.tenant_idnumber || "—" }}</span>
                    </span>
                  </router-link>
                </td>
                <td>{{ tenant.tenant_phone || "—" }}</td>
                <td>
                  <span class="dash-badge" :class="genderBadgeClass(tenant.gender)">
                    <i :class="getGenderIcon(tenant.gender)"></i>
                    {{ tenant.gender || "—" }}
                  </span>
                </td>
                <td>
                  <span class="dash-badge" :class="statusBadgeClass(tenant.marital_status)">
                    {{ tenant.marital_status || "—" }}
                  </span>
                </td>
                <td>
                  <span class="dash-badge dash-badge--primary" :title="`${tenant.unit_count || 0} units`">
                    <i class="bi bi-door-open"></i>
                    {{ tenant.unit_count || 0 }}
                  </span>
                </td>
                <td class="text-end">
                  <div class="tl-actions">
                    <router-link :to="`/tenant/${tenant.id}`" class="tl-action" title="View details">
                      <i class="bi bi-eye"></i>
                    </router-link>
                    <router-link :to="`/edit-tenant/${tenant.id}`" class="tl-action" title="Edit">
                      <i class="bi bi-pencil"></i>
                    </router-link>
                    <button class="tl-action tl-action--danger" title="Delete" @click="deleteTenant(tenant)">
                      <i class="bi bi-trash"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- EMPTY -->
        <div v-else class="dash-state">
          <i class="bi bi-people" style="font-size: 2rem;"></i>
          <p v-if="search">No tenants match "{{ search }}".</p>
          <template v-else>
            <p>No tenants yet.</p>
            <router-link to="/add/tenant" class="dash-btn dash-btn--primary dash-btn--sm">
              Add your first tenant
            </router-link>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { apiFetch } from "@/services/api.js";

// Same soft/ink pairs .dash-badge already uses, so an avatar's color never
// clashes with the badges next to it.
const AVATAR_PALETTE = [
  { bg: "var(--dash-primary-soft)", ink: "var(--dash-primary-ink)" },
  { bg: "var(--dash-success-soft)", ink: "var(--dash-success-ink)" },
  { bg: "var(--dash-warning-soft)", ink: "var(--dash-warning-ink)" },
  { bg: "var(--dash-alt-soft)", ink: "var(--dash-alt-ink)" },
];

export default {
  name: "TenantsList",

  data() {
    return {
      tenants: [],
      units: [],
      search: "",
      loading: false,
    };
  },

  computed: {
    // Units actually occupied by each tenant, keyed by tenant id. The
    // /tenants endpoint returns one row per tenant with no reliable way to
    // tell how many units they hold (see below), so the real count comes
    // from cross-referencing /units, same as tenantDetails.vue and
    // UnitsList.vue already do via unit.tenant_id.
    unitsByTenantId() {
      const map = new Map();
      this.units.forEach((unit) => {
        if (unit.tenant_id == null) return;
        if (!map.has(unit.tenant_id)) map.set(unit.tenant_id, []);
        map.get(unit.tenant_id).push(unit.id);
      });
      return map;
    },

    // De-duplicated tenant list (by id — /tenants has occasionally been
    // seen to repeat a row) with an accurate unit_count/unit_ids per
    // tenant, computed from /units rather than guessed from repeated rows
    // in /tenants (which is one row per tenant, so it never actually had
    // duplicates to count in the first place — that's why this used to
    // always show "1").
    uniqueTenants() {
      const uniqueMap = new Map();

      this.tenants.forEach((tenant) => {
        const key = tenant.id ?? tenant.tenant_idnumber;
        if (uniqueMap.has(key)) return;

        const unitIds = this.unitsByTenantId.get(tenant.id) || [];
        uniqueMap.set(key, {
          ...tenant,
          unit_count: unitIds.length,
          unit_ids: unitIds,
        });
      });

      return Array.from(uniqueMap.values());
    },

    // Filter unique tenants based on search
    filteredUniqueTenants() {
      const key = this.search.toLowerCase().trim();
      if (!key) return this.uniqueTenants;

      return this.uniqueTenants.filter(t =>
        t.tenant_name?.toLowerCase().includes(key) ||
        t.tenant_phone?.toLowerCase().includes(key) ||
        t.tenant_idnumber?.toLowerCase().includes(key)
      );
    },

    // Gender statistics
    genderStats() {
      const stats = { male: 0, female: 0, other: 0 };
      this.uniqueTenants.forEach(t => {
        const gender = t.gender?.toLowerCase();
        if (gender === 'male') stats.male++;
        else if (gender === 'female') stats.female++;
        else stats.other++;
      });
      return stats;
    },

    // Total units occupied
    totalUnits() {
      return this.uniqueTenants.reduce((sum, t) => sum + (t.unit_count || 0), 0);
    }
  },

  methods: {
    async loadTenants() {
      this.loading = true;
      try {
        const [tenantsRes, unitsRes] = await Promise.all([
          apiFetch("/tenants"),
          apiFetch("/units"),
        ]);

        // Normalize response shape
        if (Array.isArray(tenantsRes)) {
          this.tenants = tenantsRes;
        } else if (Array.isArray(tenantsRes.data)) {
          this.tenants = tenantsRes.data;
        } else if (Array.isArray(tenantsRes.tenants)) {
          this.tenants = tenantsRes.tenants;
        } else {
          this.tenants = [];
          console.warn("Unexpected tenants response shape", tenantsRes);
        }

        if (Array.isArray(unitsRes)) {
          this.units = unitsRes;
        } else if (Array.isArray(unitsRes.data)) {
          this.units = unitsRes.data;
        } else if (Array.isArray(unitsRes.units)) {
          this.units = unitsRes.units;
        } else {
          this.units = [];
          console.warn("Unexpected units response shape", unitsRes);
        }
      } catch (e) {
        console.error("Failed to load tenants", e);
        this.$toast?.error?.(e.message || "Failed to load tenants") ||
          alert("Failed to load tenants");
      } finally {
        this.loading = false;
      }
    },

    getGenderIcon(gender) {
      const g = gender?.toLowerCase();
      if (g === 'male') return 'bi bi-gender-male';
      if (g === 'female') return 'bi bi-gender-female';
      return 'bi bi-gender-ambiguous';
    },

    // Shared .dash-badge modifiers (design.css) so every table's status
    // pills read the same way — see docs/ARCHITECTURE.md.
    genderBadgeClass(gender) {
      const g = gender?.toLowerCase();
      if (g === 'male') return 'dash-badge--primary';
      if (g === 'female') return 'dash-badge--alt';
      return '';
    },
    statusBadgeClass(status) {
      const s = status?.toLowerCase();
      if (s === 'married') return 'dash-badge--success';
      return '';
    },

    getInitials(name) {
      if (!name) return "?";
      return name
        .trim()
        .split(/\s+/)
        .map((word) => word[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    },

    avatarColor(name) {
      const str = name || "?";
      let hash = 0;
      for (let i = 0; i < str.length; i++) {
        hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
      }
      return AVATAR_PALETTE[hash % AVATAR_PALETTE.length];
    },

    deleteTenant(tenant) {
      if (!confirm(`Are you sure you want to delete ${tenant.tenant_name}? This will affect ${tenant.unit_count || 0} unit(s).`)) return;

      // Implement delete functionality
      console.log("Delete tenant:", tenant.id);
      alert("Delete functionality to be implemented");
    }
  },

  mounted() {
    this.loadTenants();
  },
};
</script>

<style scoped>
.tl-search {
  position: relative;
}

.tl-search i {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--dash-faint);
  font-size: 14px;
}

.tl-search input {
  width: 260px;
  padding: 10px 16px 10px 38px;
  border-radius: var(--dash-pill);
  border: 1px solid var(--dash-line);
  background: var(--dash-surface);
  font-size: var(--dash-fs-sm);
  color: var(--dash-text);
}

.tl-search input:focus {
  outline: none;
  border-color: var(--dash-primary);
  box-shadow: 0 0 0 3px var(--dash-primary-ring);
}

.tl-stats {
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  margin-bottom: 24px;
}

/* ---------- Table: rows as floating, rounded cards ---------- */
.tl-table-wrap {
  overflow-x: auto;
}

.tl-table {
  width: 100%;
  min-width: 760px;
  border-collapse: separate;
  border-spacing: 0 10px;
}

.tl-table thead th {
  text-align: left;
  padding: 0 18px 8px;
  font-size: var(--dash-fs-micro);
  font-weight: 700;
  letter-spacing: var(--dash-track-caps);
  text-transform: uppercase;
  color: var(--dash-muted);
}

.tl-table tbody tr {
  background: var(--dash-surface);
  box-shadow: var(--dash-shadow-xs);
  transition: box-shadow var(--dash-t), transform var(--dash-t);
}

.tl-table tbody tr:hover {
  box-shadow: var(--dash-shadow);
  transform: translateY(-1px);
}

.tl-table tbody td {
  padding: 14px 18px;
  border-top: 1px solid var(--dash-line-soft);
  border-bottom: 1px solid var(--dash-line-soft);
  font-size: var(--dash-fs-sm);
  color: var(--dash-text);
  vertical-align: middle;
}

.tl-table tbody td:first-child {
  border-left: 1px solid var(--dash-line-soft);
  border-top-left-radius: var(--dash-radius);
  border-bottom-left-radius: var(--dash-radius);
}

.tl-table tbody td:last-child {
  border-right: 1px solid var(--dash-line-soft);
  border-top-right-radius: var(--dash-radius);
  border-bottom-right-radius: var(--dash-radius);
}

/* ---------- Tenant identity cell ---------- */
.tl-tenant {
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
  color: inherit;
}

.tl-avatar {
  flex: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12.5px;
  font-weight: 700;
}

.tl-tenant-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.tl-tenant-name {
  font-weight: 650;
  font-size: var(--dash-fs-body);
  color: var(--dash-ink);
  transition: color var(--dash-t);
}

.tl-tenant:hover .tl-tenant-name {
  color: var(--dash-primary);
}

.tl-tenant-id {
  font-size: var(--dash-fs-micro);
  font-family: var(--dash-font-mono);
  color: var(--dash-muted);
}

/* ---------- Actions ---------- */
.tl-actions {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
}

.tl-action {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: var(--dash-bg-alt);
  color: var(--dash-muted);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  cursor: pointer;
  transition: background var(--dash-t), color var(--dash-t);
}

.tl-action:hover {
  background: var(--dash-primary-soft);
  color: var(--dash-primary-ink);
}

.tl-action--danger:hover {
  background: var(--dash-danger-soft);
  color: var(--dash-danger-ink);
}

@media (max-width: 768px) {
  .tl-search input {
    width: 100%;
  }

  .tl-search {
    flex: 1 1 100%;
  }
}
</style>
